import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Users,
  CalendarDays,
  BarChart2,
  ArrowRight,
  ChevronRight,
  TrendingUp,
  CreditCard,
  Mail,
  MapPin,
  Calendar,
  Bell,
  ChevronLeft,
  DollarSign,
  Database,
  Beaker,
  BedDouble,
  FileText,
  List,
  PieChart,
  Layers,
  FilePlus,
  ClipboardCheck,
  ChartNoAxesCombined,
  PiggyBank,
  FileSignature,
  Pill,
  Gamepad2,
  Receipt,
} from "lucide-react";
import logoImage from "../RecoveryHousePortalLogo.png";

const LandingPage = () => {
  const navigate = useNavigate();
  const [activePlatform, setActivePlatform] = useState("recovery");
  const [forceUpdate, setForceUpdate] = useState(false);

  const containerRef = useRef(null);

  const scroll = (direction) => {
    if (containerRef.current) {
      const container = containerRef.current;
      const cardWidth = 400; // width of each card + gap
      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;

      if (direction === "left") {
        if (currentScroll === 0) {
          // If at start, jump to end
          container.scrollLeft = maxScroll;
        } else {
          container.scrollBy({ left: -cardWidth, behavior: "smooth" });
        }
      } else {
        if (currentScroll >= maxScroll) {
          // If at end, jump to start
          container.scrollLeft = 0;
        } else {
          container.scrollBy({ left: cardWidth, behavior: "smooth" });
        }
      }
    }
  };

  const platformFeatures = {
    recovery: {
      title: "Recovery House Platform",
      subtitle: "Complete recovery house management system",
      description:
        "Comprehensive tools for recovery house owners and leaders to streamline operations, manage residents, and track success metrics.",
      features: [
        {
          icon: <FileText />,
          title: "Resident Management",
          desc: "Comprehensive tracking of resident data including intake information, documentation, medication data, recovery milestones, resident notes.",
        },
        {
          icon: <DollarSign />,
          title: "Financial Tracking",
          desc: "Complete financial oversight with automated balance calculations, invoice generation, payment history tracking, and detailed financial reporting for each resident's housing fees.",
        },
        {
          icon: <FilePlus />,
          title: "Custom Forms Builder",
          desc: "Design and deploy customized forms that can be embedded anywhere or shared via URL, allowing for seamless data collection from residents and staff.",
        },
        {
          icon: <Pill />,
          title: "Medication Management",
          desc: "Comprehensive medication logging system for tracking prescriptions, dosages, schedules, and administration with detailed reporting capabilities.",
        },
        {
          icon: <Receipt />,
          title: "Invoice & Receipt Generation",
          desc: "Automatically generate professional invoices and receipts for all financial transactions, simplifying accounting and providing residents with proper documentation.",
        },
        {
          icon: <ClipboardCheck />,
          title: "Sponsorship Platform",
          desc: "Monitor and track residents' progress through 12-step work with up-to-date information on their step work, sponsor meetings, and recovery program participation.",
        },
        {
          icon: <BarChart2 />,
          title: "Recovery Analytics",
          desc: "In-depth analysis of recovery metrics including sobriety duration, MAT participation, probation/parole status, discharge outcomes, demographic data, medication statistics, and more!",
        },
        {
          icon: <PieChart />,
          title: "Owner Financial Dashboard",
          desc: "Centralized view of organizational financial data including revenue tracking, tax information, payment histories, outstanding balances, and comprehensive financial reporting.",
        },
        {
          icon: <BedDouble />,
          title: "Bed & Room Management",
          desc: "Manage bed assignments, room configurations, and occupancy",
        },
        {
          icon: <List />,
          title: "Waitlist Management",
          desc: "Organize and track potential residents efficiently",
        },
        {
          icon: <Beaker />,
          title: "UA Management",
          desc: "Comprehensive UA tracking system with automated weekly randomized schedules, detailed result history, and the ability to generate resident-specific reports across custom date ranges with PDF export functionality.",
        },
        {
          icon: <TrendingUp />,
          title: "Recovery Capital Assessment",
          desc: "Structured questionnaire system for evaluating and monitoring residents' recovery progress through periodic assessments, providing insights into their recovery and highlighting areas of growth and potential focus.",
        },
      ],
    },
    resident: {
      title: "Resident Platform",
      subtitle: "Optional resident-facing features",
      description:
        "Empower residents with tools to manage their recovery journey, track progress, and stay connected with house requirements.",
      features: [
        {
          icon: <Layers />,
          title: "Phases System",
          desc: "Create custom phase programs with specific requirements, assign them to residents, and track their progress through each phase of recovery with detailed milestone monitoring.",
        },
        {
          icon: <PiggyBank />,
          title: "Personal Budget Tool",
          desc: "Personalized budgeting system to help residents develop financial responsibility with expense tracking, savings goals, and financial planning guidance.",
        },
        {
          icon: <FileSignature />,
          title: "Forms & Document Access",
          desc: "Direct access to staff-assigned forms requiring completion, plus a comprehensive library of all previously completed and signed documents throughout residency.",
        },
        {
          icon: <MapPin />,
          title: "Geo-Location Meeting Tracking",
          desc: "Verify meeting attendance with location-based check-ins",
        },
        {
          icon: <Calendar />,
          title: "Request Management",
          desc: "Submit and track overnight requests and special permissions",
        },
        {
          icon: <ChartNoAxesCombined />,
          title: "Personal Progress Dashboard",
          desc: "View recovery capital scores, UA history, and progress metrics",
        },
        {
          icon: <CreditCard />,
          title: "Payment History",
          desc: "Access rent history and payment records",
        },
        {
          icon: <Bell />,
          title: "Notifications",
          desc: "Receive important updates and reminders",
        },
        {
          icon: <CalendarDays />,
          title: "Community Schedule",
          desc: "Access house schedule and community information",
        },
        {
          icon: <Gamepad2 />,
          title: "Cognitive Recovery Games",
          desc: "Access engaging brain-training activities designed to improve cognitive function during recovery, with personalized tracking of progress in memory, attention, and processing speed over time.",
        },
      ],
    },
  };

  return (
    <>
      <Helmet>
        <title>&bull; Recovery House Platform</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 relative overflow-hidden">
        {/* Existing ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        {/* Navigation */}
        <nav className="border-b border-slate-800/50 bg-zinc-900/95 z-50 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16">
              {/* Logo Section */}
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <div className="flex gap-1">
                  <div className="h-6 sm:h-8 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
                  <div className="h-6 sm:h-8 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
                  <div className="h-6 sm:h-8 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
                </div>
                <span className="text-white font-bold text-base sm:text-lg md:text-xl">
                  Recovery House Portal
                </span>
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center gap-2 sm:gap-4">
                <button
                  onClick={() => navigate("/login")}
                  className="px-3 sm:px-4 py-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-lg transition-all duration-300 flex items-center gap-1 sm:gap-2 text-sm sm:text-base whitespace-nowrap"
                >
                  <span className="hidden sm:inline">Login</span>
                  <span className="sm:hidden">Login</span>
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                </button>

                <a
                  href="mailto:recoveryhouseplatform@gmail.com"
                  className="p-2 sm:px-4 sm:py-2 text-slate-300 hover:text-white transition-colors flex items-center gap-1 sm:gap-2"
                >
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline">Contact Us</span>
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Enhanced Hero Section */}
        <div className="relative py-16 md:py-24 overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full filter blur-[100px] opacity-30" />
            <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-indigo-600/20 rounded-full filter blur-[100px] opacity-30" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8ZGVmcz4KICA8cGF0dGVybiBpZD0iZ3JpZCIgeD0iMCIgeT0iMCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj4KICAgIDxwYXRoIGQ9Ik0wIDEwIEw0MCAxMCIgc3Ryb2tlPSIjMDYyNDQ3IiBzdHJva2Utd2lkdGg9IjAuMyIgZmlsbD0ibm9uZSIgb3BhY2l0eT0iMC4yIi8+CiAgICA8cGF0aCBkPSJNMCAyMCBMNDAgMjAiIHN0cm9rZT0iIzA2MjQ0NyIgc3Ryb2tlLXdpZHRoPSIwLjMiIGZpbGw9Im5vbmUiIG9wYWNpdHk9IjAuMiIvPgogICAgPHBhdGggZD0iTTAgMzAgTDQwIDMwIiBzdHJva2U9IiMwNjI0NDciIHN0cm9rZS13aWR0aD0iMC4zIiBmaWxsPSJub25lIiBvcGFjaXR5PSIwLjIiLz4KICAgIDxwYXRoIGQ9Ik0xMCAwIEwxMCA0MCIgc3Ryb2tlPSIjMDYyNDQ3IiBzdHJva2Utd2lkdGg9IjAuMyIgZmlsbD0ibm9uZSIgb3BhY2l0eT0iMC4yIi8+CiAgICA8cGF0aCBkPSJNMjAgMCBMMjAgNDAiIHN0cm9rZT0iIzA2MjQ0NyIgc3Ryb2tlLXdpZHRoPSIwLjMiIGZpbGw9Im5vbmUiIG9wYWNpdHk9IjAuMiIvPgogICAgPHBhdGggZD0iTTMwIDAgTDMwIDQwIiBzdHJva2U9IiMwNjI0NDciIHN0cm9rZS13aWR0aD0iMC4zIiBmaWxsPSJub25lIiBvcGFjaXR5PSIwLjIiLz4KICA8L3BhdHRlcm4+CjwvZGVmcz4KPHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIgb3BhY2l0eT0iMC4xNSIvPgo8L3N2Zz4=')] opacity-20" />
          </div>

          <div className="container mx-auto px-6 relative">
            <div className="max-w-4xl mx-auto">
              <div className="text-center relative z-10">
                {/* Main Logo with Glow Effect */}
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 rounded-3xl filter blur-3xl opacity-20 scale-105" />
                  <img
                    src={logoImage}
                    alt="Recovery House Portal Logo"
                    className="mx-auto w-full max-w-2xl sm:max-w-3xl h-auto relative"
                  />
                </div>

                {/* Headline */}
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                  Complete{" "}
                  <span className="bg-gradient-to-r from-cyan-400 to-cyan-600 bg-clip-text text-transparent">
                    Recovery House
                  </span>{" "}
                  Management Solution
                </h1>

                {/* Description */}
                <p className="text-lg md:text-xl text-slate-300 mb-6 mx-auto max-w-3xl">
                  <span className="font-semibold text-cyan-400">
                    Purpose-built for sober living homes
                  </span>{" "}
                  — not treatment facilities. Every feature is specifically
                  designed for the unique challenges of recovery residences.
                </p>

                <p className="text-lg text-slate-300 mb-8 mx-auto max-w-3xl">
                  Choose the platform that works best for your operation. Our
                  core Recovery House Portal streamlines management, while the
                  optional Resident Platform empowers your community.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
                  <button
                    onClick={() => navigate("/login")}
                    className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-white rounded-lg font-medium transition-all duration-300 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30 flex items-center justify-center gap-2 transform hover:-translate-y-1"
                  >
                    <span>Start Free Trial</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </button>

                  <a
                    href="mailto:recoveryhouseplatform@gmail.com"
                    className="px-8 py-3 bg-slate-800/60 backdrop-blur-sm hover:bg-slate-700/60 text-white border border-slate-700 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 transform hover:-translate-y-1"
                  >
                    <span>Contact for Demo</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Platform Selection Tabs - Integrated with Hero */}
        <div className="relative z-10 pb-20">
          <div className="max-w-7xl mx-auto px-6 relative">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white mb-4">
                Choose Your{" "}
                <span className="bg-gradient-to-r from-cyan-400 to-cyan-600 bg-clip-text text-transparent">
                  Platform
                </span>
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto">
                Select the solution that works best for your recovery residence
                operation.
              </p>
            </div>

            {/* Platform Selection Buttons */}
            <div className="flex flex-col md:flex-row justify-center gap-4 mb-12">
              {Object.entries(platformFeatures).map(([key, platform]) => (
                <button
                  key={key}
                  onClick={() => setActivePlatform(key)}
                  className={`w-full md:w-80 px-8 py-5 rounded-xl transition-all duration-300 backdrop-blur-sm border ${
                    activePlatform === key
                      ? "bg-gradient-to-r from-cyan-500 to-cyan-600 text-white border-cyan-500/30 shadow-lg shadow-cyan-500/10"
                      : "bg-slate-800/80 text-slate-300 hover:bg-slate-700/80 border-slate-700/50 hover:border-slate-600/50"
                  }`}
                >
                  <div className="flex flex-col items-center text-center">
                    <span className="text-lg font-semibold mb-1">
                      {platform.title}
                    </span>
                    <span className="text-sm opacity-80">
                      {platform.subtitle}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Active Platform Details */}
            <div className="mb-16">
              <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-800/70 rounded-2xl p-8 transition-all duration-500">
                <div className="text-center mb-10">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    {platformFeatures[activePlatform].subtitle}
                  </h3>

                  <p className="text-slate-300 max-w-2xl mx-auto mb-4">
                    {platformFeatures[activePlatform].description}
                  </p>

                  {platformFeatures[activePlatform].monthlyPrice && (
                    <div className="text-cyan-400 text-xl font-semibold">
                      {platformFeatures[activePlatform].monthlyPrice}
                    </div>
                  )}
                </div>

                {/* Feature Carousel */}
                <div className="relative max-w-7xl mx-auto">
                  {/* Navigation Buttons - With updated styling */}
                  <button
                    onClick={() => scroll("left")}
                    className="absolute -left-6 md:-left-12 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-slate-800/90 border border-slate-700/50 hover:bg-gradient-to-r hover:from-cyan-500 hover:to-cyan-600 text-slate-300 hover:text-white shadow-lg hover:shadow-xl transition-all duration-300 group"
                    aria-label="Scroll left"
                  >
                    <ChevronLeft className="h-6 w-6 group-hover:scale-110 transition-transform duration-200" />
                  </button>

                  <button
                    onClick={() => scroll("right")}
                    className="absolute -right-6 md:-right-12 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-slate-800/90 border border-slate-700/50 hover:bg-gradient-to-r hover:from-cyan-500 hover:to-cyan-600 text-slate-300 hover:text-white shadow-lg hover:shadow-xl transition-all duration-300 group"
                    aria-label="Scroll right"
                  >
                    <ChevronRight className="h-6 w-6 group-hover:scale-110 transition-transform duration-200" />
                  </button>

                  {/* Scrollable Container */}
                  <div
                    ref={containerRef}
                    className="flex overflow-x-auto gap-6 py-8 snap-x snap-mandatory no-scrollbar relative"
                    style={{
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      WebkitOverflowScrolling: "touch",
                    }}
                    onScroll={() => setForceUpdate((prev) => !prev)}
                  >
                    {platformFeatures[activePlatform].features.map(
                      (feature, index) => (
                        <div
                          key={index}
                          className="flex-none w-[300px] md:w-[400px] snap-center transform transition-all duration-300 hover:translate-y-[-4px]"
                        >
                          <div className="h-full bg-slate-900/60 backdrop-blur-xl rounded-xl p-8 border border-slate-800/70 hover:border-cyan-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/5 group">
                            <div className="p-4 bg-slate-800/70 rounded-lg w-fit mb-6 group-hover:bg-cyan-500/10 transition-all duration-500">
                              <div className="text-cyan-400 h-7 w-7 transform group-hover:scale-110 transition-transform duration-500">
                                {feature.icon}
                              </div>
                            </div>
                            <div className="space-y-4">
                              <h3 className="text-xl font-semibold text-white tracking-tight group-hover:text-cyan-400 transition-colors duration-300">
                                {feature.title}
                              </h3>
                              <p className="text-slate-300 leading-relaxed">
                                {feature.desc}
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Position Indicators - Updated styling */}
                  <div className="flex justify-center gap-2 mt-8">
                    {platformFeatures[activePlatform].features.map(
                      (_, index) => {
                        const isActive =
                          index ===
                          Math.floor(
                            (containerRef.current?.scrollLeft || 0) /
                              (containerRef.current?.offsetWidth || 1)
                          );

                        return (
                          <button
                            key={index}
                            onClick={() => {
                              if (containerRef.current) {
                                const slideWidth =
                                  containerRef.current.offsetWidth;
                                containerRef.current.scrollTo({
                                  left: slideWidth * index,
                                  behavior: "smooth",
                                });
                              }
                            }}
                            className={`
                    transition-all duration-300 rounded-full cursor-pointer
                    ${
                      isActive
                        ? "w-8 h-2 bg-gradient-to-r from-cyan-500 to-cyan-400 shadow-lg shadow-cyan-500/30"
                        : "w-2 h-2 bg-slate-700 hover:bg-slate-600"
                    }
                  `}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        );
                      }
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="w-full relative z-50 mt-20">
          {/* Simple border top with slight glow */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />

          {/* Footer content with distinct background */}
          <div className="bg-[#0A0A0F] py-12">
            <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Company Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-white">
                  Recovery House Platform
                </h3>
                <p className="text-gray-400 leading-relaxed max-w-md">
                  A complete solution for managing recovery houses, tracking
                  resident progress, and building lasting success.
                </p>
                <p className="text-gray-400 leading-relaxed max-w-md pt-4">
                  Contact us at recoveryhouseplatform@gmail.com
                </p>
              </div>

              {/* Quick Links */}
              <div className="flex justify-end">
                <div className="space-y-4">
                  <h4 className="text-white font-medium">Quick Links</h4>
                  <ul className="space-y-2 text-gray-400">
                    <li>
                      <Link
                        to="/privacy-policy"
                        className="hover:text-cyan-400 transition-colors"
                      >
                        Privacy Policy
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/terms-of-service"
                        className="hover:text-cyan-400 transition-colors"
                      >
                        Terms of Service
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/faq"
                        className="hover:text-cyan-400 transition-colors"
                      >
                        FAQ
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/dpa"
                        className="hover:text-cyan-400 transition-colors"
                      >
                        DPA
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 mt-12 pt-6 border-t border-slate-800/50">
              <p className="text-gray-500 text-sm text-center md:text-left">
                © 2024 Recovery House Platform. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
